import React, { useMemo, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { ProductCard } from '../components/product/ProductCard';
import { mockProducts } from '../data/mockProducts';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
const categories = ['All', 'Flours & Grains', 'Fresh Produce', 'Spices & Seasonings', 'Oils & Sauces'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300
    }
  }
};
const pillVariants = {
  inactive: {
    backgroundColor: 'rgb(255, 255, 255)',
    color: 'rgb(75, 85, 99)',
    scale: 1
  },
  active: {
    backgroundColor: 'rgb(255, 107, 53)',
    color: 'rgb(255, 255, 255)',
    scale: 1.05
  }
};
export function SearchPage() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showWICOnly, setShowWICOnly] = useState(false);
  const [showEBTOnly, setShowEBTOnly] = useState(false);
  const filteredProducts = useMemo(() => {
    return mockProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
      const matchesWIC = !showWICOnly || product.isWIC;
      const matchesEBT = !showEBTOnly || product.isEBT;
      return matchesSearch && matchesCategory && matchesWIC && matchesEBT;
    });
  }, [searchQuery, selectedCategory, showWICOnly, showEBTOnly]);
  return <div className="min-h-screen bg-[#FAF8F5] pb-24 md:pb-8" data-id="element-375">
      <TopNav data-id="element-376" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8" data-id="element-377">
        {/* Search Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="mb-6" data-id="element-378">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-379">
            Shop All Products
          </h1>

          {/* Search Bar */}
          <div className="relative mb-4" data-id="element-380">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-381" />
            <input type="text" placeholder="Search for products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-base focus:ring-2 focus:ring-[#ff6b35] focus:border-transparent shadow-sm transition-all" data-id="element-382" />
          </div>

          {/* Category Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide mb-4" data-id="element-383">
            {categories.map(category => <motion.button key={category} onClick={() => setSelectedCategory(category)} variants={pillVariants} initial="inactive" animate={selectedCategory === category ? 'active' : 'inactive'} transition={{
            type: 'spring',
            damping: 20,
            stiffness: 300
          }} className="px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap border border-gray-200 shadow-sm" data-id="element-384">
                {category}
              </motion.button>)}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap" data-id="element-385">
            <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-386">
              <SlidersHorizontal className="w-4 h-4" data-id="element-387" />
              <span className="font-medium" data-id="element-388">Filters:</span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-id="element-389">
              <input type="checkbox" checked={showWICOnly} onChange={e => setShowWICOnly(e.target.checked)} className="w-4 h-4 text-[#ff6b35] border-gray-300 rounded focus:ring-[#ff6b35]" data-id="element-390" />
              <span className="text-sm font-medium text-gray-700" data-id="element-391">
                WIC Only
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-id="element-392">
              <input type="checkbox" checked={showEBTOnly} onChange={e => setShowEBTOnly(e.target.checked)} className="w-4 h-4 text-[#ff6b35] border-gray-300 rounded focus:ring-[#ff6b35]" data-id="element-393" />
              <span className="text-sm font-medium text-gray-700" data-id="element-394">
                EBT Only
              </span>
            </label>
          </div>
        </motion.div>

        {/* Results Count */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} className="mb-6" data-id="element-395">
          <p className="text-sm text-gray-600" data-id="element-396">
            {filteredProducts.length}{' '}
            {filteredProducts.length === 1 ? 'product' : 'products'} found
          </p>
        </motion.div>

        {/* Products Grid */}
        <AnimatePresence mode="wait" data-id="element-397">
          <motion.div key={`${selectedCategory}-${showWICOnly}-${showEBTOnly}-${searchQuery}`} initial="hidden" animate="visible" exit="hidden" variants={containerVariants} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-398">
            {filteredProducts.map(product => <motion.div key={product.id} variants={itemVariants} data-id="element-399">
                <ProductCard product={product} data-id="element-400" />
              </motion.div>)}
          </motion.div>
        </AnimatePresence>

        {/* Empty State */}
        {filteredProducts.length === 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center py-16" data-id="element-401">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-402">
              <Search className="w-8 h-8 text-gray-400" data-id="element-403" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-404">
              No products found
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-405">
              Try adjusting your filters or search terms
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSelectedCategory('All');
          setShowWICOnly(false);
          setShowEBTOnly(false);
        }} className="bg-[#ff6b35] text-white font-semibold px-6 py-3 rounded-xl hover:bg-[#e85a2a] transition-colors" data-id="element-406">
              Clear All Filters
            </button>
          </motion.div>}
      </div>

      <BottomNav data-id="element-407" />
    </div>;
}